/**
 * Setup's driver form ↔ the backend's driver row.
 *
 * ⛔ SETUP'S DRIVER FORM USED TO SAVE NOTHING. "Add driver", "Save changes", the consent box on
 * an imported driver and "Remove" only changed React state, and Finish wrote the contacts and
 * nothing else. A driver typed in by hand vanished on refresh, and an imported driver could never
 * be given consent from the screen that asks for it, so Alfred could never call them. The routes
 * (POST/PATCH/DELETE /api/carrier/drivers) existed the whole time.
 *
 * The form keeps its own names (truckNo, ecName, quiet "9:00p – 6:00a", zone "Central"); the
 * backend stores snake_case columns, quiet hours as two HH:MM values and an IANA timezone. This
 * translates both ways, here, where it can be tested.
 *
 * ⛔ A MISSING FIELD STAYS MISSING. A stored driver with no trailer type or language comes back
 * as "", not as the form's default, so opening and saving a driver cannot write values nobody
 * chose. The Select shows such a value as "— not set —".
 */

export const LANGUAGES = ["English", "Spanish", "English and Spanish", "Other"];
export const TRAILERS = ["53' dry van", "53' reefer", "48' flatbed", "Step deck", "Tanker", "Power only", "Other"];
export const ZONES = ["Eastern", "Central", "Mountain", "Pacific", "Arizona (no DST)"];
// The picker shows a label; scheduling and quiet hours need a real IANA zone, so the
// screen translates rather than storing "Central" and hoping something downstream guesses.
export const ZONE_TZ = {
  Eastern: "America/New_York", Central: "America/Chicago", Mountain: "America/Denver",
  Pacific: "America/Los_Angeles", "Arizona (no DST)": "America/Phoenix"
};
export const NO_QUIET = "No quiet hours";
export const QUIET = ["9:00p – 6:00a", "10:00p – 5:00a", "8:00p – 7:00a", NO_QUIET];

/** The words beside the consent box — and, verbatim, what is recorded as consent_text. */
export const DRIVER_CONSENT_TEXT =
  "This driver has been told Alfred is an AI, that calls are recorded and transcribed, and that " +
  "anything they say about how they're doing may be passed to the office.";

export const BLANK_DRIVER = {
  name: "", phone: "", language: "English", truckNo: "", truckDesc: "",
  trailerType: "53' dry van", trailerNo: "", zone: "Central", quiet: "9:00p – 6:00a",
  ecName: "", ecPhone: "", consent: false, note: "",
};

const digits = (p) => String(p ?? "").replace(/\D/g, "");

/** Same phone when the last ten digits match, with or without the +1. */
export function samePhone(a, b) {
  const x = digits(a).slice(-10);
  return x.length === 10 && x === digits(b).slice(-10);
}

/** "21:00" → "9:00p". Null for anything unreadable. */
function to12(hhmm) {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(hhmm || ""));
  if (!m) return null;
  const h = Number(m[1]);
  return `${h % 12 === 0 ? 12 : h % 12}:${m[2]}${h >= 12 ? "p" : "a"}`;
}

/** "9:00p" → "21:00". Null for anything unreadable. */
function to24(t) {
  const m = /^(\d{1,2}):(\d{2})\s*([ap])/i.exec(String(t || "").trim());
  if (!m) return null;
  let h = Number(m[1]);
  if (h < 1 || h > 12 || Number(m[2]) > 59) return null;
  const pm = m[3].toLowerCase() === "p";
  h = pm ? (h === 12 ? 12 : h + 12) : (h === 12 ? 0 : h);
  return `${String(h).padStart(2, "0")}:${m[2]}`;
}

/** Stored window → the label the form shows, in the same "9:00p – 6:00a" shape as QUIET. */
export function quietLabelFor(start, end) {
  if (!start && !end) return NO_QUIET;
  const a = to12(start);
  const b = to12(end);
  return a && b ? `${a} – ${b}` : NO_QUIET;
}

/** The form's label → { start, end }. "No quiet hours" and anything unreadable → nulls. */
export function quietWindowFor(label) {
  const parts = String(label || "").split(/\s*[–—-]\s*/);
  if (parts.length !== 2) return { start: null, end: null };
  const start = to24(parts[0]);
  const end = to24(parts[1]);
  return start && end ? { start, end } : { start: null, end: null };
}

/** A driver as GET /api/carrier/drivers returns it → the form. */
export function driverFormFromApi(d) {
  const zoneFromTz = Object.keys(ZONE_TZ).find((z) => ZONE_TZ[z] === d.timezone);
  return {
    id: d.id,
    name: d.name || "",
    phone: d.phone || d.phoneE164 || "",
    phoneE164: d.phoneE164 || null,
    language: d.language || "",
    truckNo: d.truck || "",
    truckDesc: (d.profile && d.profile.truckDesc) || "",
    trailerType: d.trailerType || "",
    trailerNo: d.trailerNo || "",
    // A stored label this form does not list stays as typed; only a missing one is recovered
    // from the timezone. Replacing "Gulf Coast" with "Central" would rewrite it on save.
    zone: ZONES.includes(d.zone) ? d.zone : (d.zone || zoneFromTz || ""),
    // Kept so a zone label this form does not know does not erase the stored timezone.
    timezone: d.timezone || null,
    quiet: quietLabelFor(d.quietStart, d.quietEnd),
    ecName: d.ecName || "",
    ecPhone: d.ecPhone || "",
    consent: d.consent === true,
    note: d.note || "",
  };
}

export const driversFormFromApi = (rows) => (rows || []).map(driverFormFromApi);

/** The form → the body POST/PATCH /api/carrier/drivers expects. */
export function driverFormToApi(f) {
  const quiet = quietWindowFor(f.quiet);
  const trim = (v) => String(v ?? "").trim();
  const truckDesc = trim(f.truckDesc);
  return {
    name: trim(f.name),
    phone: trim(f.phone),
    language: trim(f.language) || null,
    truck_no: trim(f.truckNo) || null,
    trailer_type: trim(f.trailerType) || null,
    trailer_no: trim(f.trailerNo) || null,
    zone: trim(f.zone) || null,
    timezone: ZONE_TZ[f.zone] || f.timezone || null,
    quiet_start: quiet.start,
    quiet_end: quiet.end,
    ec_name: trim(f.ecName) || null,
    ec_phone: trim(f.ecPhone) || null,
    consent: f.consent === true,
    // What the dispatcher saw beside the box, stored with the consent. Never sent without it.
    consentText: f.consent === true ? DRIVER_CONSENT_TEXT : null,
    note: trim(f.note) || null,
    // Always the key, so clearing the field erases it: the backend MERGES profile, and an empty
    // object would leave the old make and model in place.
    profile: { truckDesc: truckDesc || null },
  };
}

/**
 * The driver already on file with this number, other than the one being edited.
 * ⛔ A new driver typed with an existing driver's number used to overwrite that driver (the add was
 * an upsert keyed on the phone). The server now refuses it with 409, removed drivers included;
 * this is the fast answer for the ones on screen, before a request is made.
 */
export function driverPhoneClash(drivers, phone, exceptId) {
  return (drivers || []).find((x) => x.id !== exceptId && samePhone(x.phoneE164 || x.phone, phone)) || null;
}

/** The backend's refusal codes, in words a dispatcher can act on. */
export function driverErrorText(err) {
  const code = err?.body?.error;
  if (code === "invalid_phone") return "That isn't a number Alfred can dial. Use a 10-digit US cell number.";
  if (code === "name_required") return "The driver needs a name.";
  if (code === "no_carrier") return "Save your carrier details first.";
  if (code === "not_found") return "That driver is no longer on file. Reload the page.";
  if (code === "phone_taken") return err.body.message || "Another driver already has that number.";
  return err?.message || "Could not save the driver.";
}
